import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { toast } from '../hooks/use-toast';
import { Mail, MailCheck, MailOpen, MousePointerClick, AlertTriangle, Ban, ArrowLeft, Loader2, RefreshCw, TrendingUp, FlaskConical, Trophy, ChevronRight, Download, Link2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import CampaignDrillDown from './CampaignDrillDown';
import ABTestModal from './ABTestModal';
import { formatDate, formatDateTime } from './kit/time';
import LongTextCell from '@/components/LongTextCell';
import { campaignLabel } from './campaignNames';
import { downloadCsv } from './kit/csv';
import PickSelect from './shared/PickSelect';

// Days are UTC, the same days the server counts. Today is one of the N, so "last 7 days" starts 6 days back.
const daysAgo = (n) => new Date(Date.now() - (n - 1) * 86400000).toISOString().slice(0, 10);
const PRESETS = [['7 days', 7], ['30 days', 30], ['90 days', 90]];

// Small utility so we don't ship a chart library for a simple bar viz.
const Bar = ({ pct, color = '#8DC63F' }) => (
  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
  </div>
);

const KPI = ({ label, value, sub, icon: Icon, color, testid }) => (
  <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
        <div className="font-serif text-3xl font-bold mt-1" style={{ color: color || '#070B5F' }}>{value}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
      </div>
      {Icon && <div className="p-2 rounded-lg" style={{ background: (color || '#070B5F') + '15', color: color || '#070B5F' }}><Icon size={18} /></div>}
    </div>
  </div>
);

export default function EmailAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ campaigns: [], totals: {} });
  const [abTests, setAbTests] = useState([]);
  const [status, setStatus] = useState({ configured: false });
  const [drillDownId, setDrillDownId] = useState(null);
  const [showAbModal, setShowAbModal] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [linkCampaign, setLinkCampaign] = useState('');
  const [links, setLinks] = useState({ links: [], limit: 0 });
  const [linksLoading, setLinksLoading] = useState(true);
  const latest = useRef(0);

  const { campaigns = [], totals = {} } = data;

  const badRange = Boolean(start && end && start > end);
  const range = { ...(start && { start }), ...(end && { end }) };

  const load = async () => {
    if (badRange) return;
    const mine = ++latest.current;
    setLoading(true);
    setLinksLoading(true);
    try {
      const [analytics, statusRes, abRes, linkRes] = await Promise.all([
        api.get('/email/analytics', { params: range }),
        api.get('/email/status'),
        api.get('/email/ab-tests'),
        api.get('/email/analytics/links', { params: { ...range, ...(linkCampaign && { campaign_id: linkCampaign }) } }),
      ]);
      if (mine !== latest.current) return; // a newer range or campaign was asked for meanwhile
      setData(analytics.data);
      setStatus(statusRes.data);
      setAbTests(abRes.data);
      setLinks(linkRes.data);
    } catch (e) {
      if (mine !== latest.current) return;
      toast({ title: 'Failed to load analytics', description: e.response?.data?.detail || 'Try again' });
    } finally {
      if (mine === latest.current) {
        setLoading(false);
        setLinksLoading(false);
      }
    }
  };
  useEffect(() => { load(); }, [start, end, linkCampaign]); // eslint-disable-line react-hooks/exhaustive-deps

  const rangeLabel = start || end ? `${start || 'start'}_to_${end || 'today'}` : 'all-time';
  const exportCampaigns = () => downloadCsv(`email-campaigns-${rangeLabel}.csv`, [
    ['Campaign', 'Sent', 'Delivered', 'Opens', 'Unique opens', 'Open rate %', 'Clicks', 'Unique clicks', 'Click rate %', 'Click-to-open %', 'Bounces', 'Bounce rate %', 'Unsubscribes', 'Spam complaints', 'Last event'],
    ...campaigns.map((c) => [
      campaignLabel(c.campaign_id, abTests), c.sent, c.delivered, c.opens, c.unique_opens, c.open_rate, c.clicks,
      c.unique_clicks, c.click_rate, c.ctor, c.bounces, c.bounce_rate, c.unsubscribes, c.complaints, c.last_event_at || '',
    ]),
  ]);
  const exportLinks = () => downloadCsv(`email-link-clicks-${rangeLabel}.csv`, [
    ['Link', 'Clicks', 'People who clicked'],
    ...links.links.map((l) => [l.url, l.clicks, l.unique_clicks]),
  ]);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="email-analytics-page">
      <div className="flex items-center justify-between mb-4">
        <Link to="/portal/marketing" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-700 text-sm" data-testid="back-to-marketing">
          <ArrowLeft size={14} /> Back to Marketing Studio
        </Link>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAbModal(true)}
            data-testid="new-ab-test"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white shadow-sm hover:brightness-110"
            style={{ background: 'linear-gradient(135deg, #6BA82E 0%, #8DC63F 100%)' }}
          >
            <FlaskConical size={12} /> New A/B Test
          </button>
          <button
            onClick={exportCampaigns}
            disabled={campaigns.length === 0}
            data-testid="export-campaigns"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            <Download size={12} /> Export CSV
          </button>
          <button
            onClick={load}
            data-testid="refresh-analytics"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Header banner */}
      <div className="rounded-2xl p-6 mb-6" style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}>
        <div className="text-[10px] uppercase tracking-widest text-white/60">Marketing Suite</div>
        <div className="font-serif text-3xl font-bold text-white">Email Analytics</div>
        <div className="mt-1 text-sm text-white/70">Track opens, clicks and bounces across every campaign you send.</div>
        {!status.configured && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-400/40 text-amber-100 text-[11px]" data-testid="email-not-configured">
            <AlertTriangle size={12} /> Email is not connected on this server yet, so no live data will show. Ask your platform administrator to turn on the Mailgun connection.
          </div>
        )}
      </div>

      {/* Date range — filters the totals, the campaigns table and the link report */}
      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="date-range">
        <label className="text-[11px] font-semibold text-slate-500 flex flex-col gap-1">
          From
          <Input type="date" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} data-testid="range-start" className="w-auto h-auto px-2 py-1 text-[12px] md:text-[12px] text-slate-700 font-normal" />
        </label>
        <label className="text-[11px] font-semibold text-slate-500 flex flex-col gap-1">
          To
          <Input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} data-testid="range-end" className="w-auto h-auto px-2 py-1 text-[12px] md:text-[12px] text-slate-700 font-normal" />
        </label>
        <div className="flex gap-1.5">
          {PRESETS.map(([label, n]) => (
            <button key={n} onClick={() => { setStart(daysAgo(n)); setEnd(''); }} data-testid={`range-last-${n}`} className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50">Last {label}</button>
          ))}
          <button onClick={() => { setStart(''); setEnd(''); }} disabled={!start && !end} data-testid="range-all" className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50">All time</button>
        </div>
        <div className="text-[11px] text-slate-400 pb-1.5">Days are UTC. Subject line A/B tests always show every event.</div>
        {badRange && <div className="text-[11px] font-semibold text-red-600 pb-1.5" role="alert" data-testid="range-error">“From” is after “To” — pick an earlier start or a later end.</div>}
      </div>

      {/* KPI grid — global totals */}
      <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <KPI label="Delivered" value={(totals.delivered || 0).toLocaleString()} sub={`${(totals.sent || 0).toLocaleString()} sent`} icon={MailCheck} color="#8DC63F" testid="kpi-delivered" />
        <KPI label="Open Rate" value={`${totals.open_rate || 0}%`} sub={`${(totals.unique_opens || 0).toLocaleString()} unique opens`} icon={MailOpen} color="#3b82f6" testid="kpi-open-rate" />
        <KPI label="Click Rate" value={`${totals.click_rate || 0}%`} sub={`${(totals.unique_clicks || 0).toLocaleString()} unique clicks`} icon={MousePointerClick} color="#6366f1" testid="kpi-click-rate" />
        <KPI label="Bounce Rate" value={`${totals.bounce_rate || 0}%`} sub={`${(totals.bounces || 0).toLocaleString()} bounces`} icon={AlertTriangle} color="#ef4444" testid="kpi-bounce-rate" />
        <KPI label="Unsubscribes" value={(totals.unsubscribes || 0).toLocaleString()} sub={`${totals.unsubscribe_rate || 0}% of delivered`} icon={Ban} color="#64748b" testid="kpi-unsubscribes" />
        <KPI label="Spam Complaints" value={(totals.complaints || 0).toLocaleString()} sub={`${totals.complaint_rate || 0}% of delivered`} icon={AlertTriangle} color="#dc2626" testid="kpi-complaints" />
      </div>

      {/* A/B Tests panel — surfaces auto-picked winner */}
      {abTests.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-6 overflow-hidden" data-testid="ab-tests-panel">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <FlaskConical size={14} style={{ color: '#6BA82E' }} />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>Subject Line A/B Tests</div>
              <div className="text-sm text-slate-500">{abTests.length} test{abTests.length !== 1 ? 's' : ''} · the winner is the subject line with more clicks</div>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {abTests.map((t) => {
              const winnerIsA = t.winner === 'A';
              const winnerIsB = t.winner === 'B';
              const pending = t.winner === 'pending';
              return (
                <div key={t.ab_test_id} className="p-5" data-testid={`ab-test-${t.ab_test_id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                    <div>
                      <div className="font-serif font-bold text-lg" style={{ color: '#070B5F' }}>{t.name}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">Launched {formatDate(t.created_at)} · {t.variant_a_count + t.variant_b_count} recipients</div>
                    </div>
                    {pending ? (
                      <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600" data-testid={`ab-status-${t.ab_test_id}`}>
                        <Loader2 size={11} className="animate-spin" /> Awaiting data (10+ delivered/variant)
                      </div>
                    ) : t.winner === 'tie' ? (
                      <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">Tie</div>
                    ) : (
                      <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full text-white" style={{ background: '#8DC63F' }} data-testid={`ab-winner-${t.ab_test_id}`}>
                        <Trophy size={11} /> Winner: Variant {t.winner} · +{t.lift_pct} pts click rate
                      </div>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    {[{ key: 'A', subject: t.subject_a, cid: t.campaign_a, stats: t.stats_a, color: '#3b82f6' },
                      { key: 'B', subject: t.subject_b, cid: t.campaign_b, stats: t.stats_b, color: '#6366f1' }].map((v) => {
                      const isWinner = (v.key === 'A' && winnerIsA) || (v.key === 'B' && winnerIsB);
                      return (
                        <button
                          key={v.key}
                          onClick={() => setDrillDownId(v.cid)}
                          data-testid={`ab-variant-${t.ab_test_id}-${v.key}`}
                          className="text-left p-3 rounded-xl border-2 transition hover:brightness-105"
                          style={{ borderColor: isWinner ? '#8DC63F' : (v.color + '30'), background: isWinner ? '#8DC63F10' : 'white' }}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-bold text-white" style={{ background: v.color }}>{v.key}</span>
                            {isWinner && <Trophy size={12} style={{ color: '#8DC63F' }} />}
                            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold ml-auto">Click for events →</span>
                          </div>
                          <div className="text-[13px] font-semibold text-slate-800 truncate" title={v.subject}>{v.subject}</div>
                          <div className="mt-2 flex gap-4 text-[11px] text-slate-600">
                            <span>{v.stats.delivered} delivered</span>
                            <span className="font-semibold" style={{ color: v.color }}>{v.stats.click_rate}% click</span>
                            <span className="text-slate-400" title="Apple Mail loads images before anyone reads, so opens run high">{v.stats.open_rate}% open</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Campaigns table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="campaigns-table">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>Campaigns</div>
            <div className="text-sm text-slate-500">{campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''} tracked</div>
          </div>
          <TrendingUp size={16} className="text-slate-300" />
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-slate-500 text-sm"><Loader2 size={14} className="animate-spin" /> Loading analytics…</div>
        ) : campaigns.length === 0 ? (
          <div className="p-10 text-center" data-testid="analytics-empty">
            <Mail size={28} className="mx-auto text-slate-300 mb-2" />
            <div className="text-sm font-semibold text-slate-700">No campaign data yet</div>
            <div className="text-[12px] text-slate-500 mt-1 max-w-md mx-auto">
              Send one from <Link to="/portal/marketing" className="font-semibold hover:underline" style={{ color: '#070B5F' }}>Marketing Studio</Link> (write an email, then Send…) or start a New A/B Test above. Opens, clicks and bounces show up here once Mailgun delivers it.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="text-left px-4 py-2.5 font-semibold">Campaign</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Sent</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Delivered</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Open Rate</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Click Rate</th>
                  <th className="text-right px-3 py-2.5 font-semibold">CTOR</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Bounces</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Unsubs</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Complaints</th>
                  <th className="text-left  px-3 py-2.5 font-semibold">Engagement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {campaigns.map((c) => (
                  <tr
                    key={c.campaign_id}
                    onClick={() => setDrillDownId(c.campaign_id)}
                    className="hover:bg-slate-50/60 transition cursor-pointer"
                    data-testid={`campaign-row-${c.campaign_id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <div>
                          <LongTextCell id={`campaign-${c.campaign_id}`} text={campaignLabel(c.campaign_id, abTests)} lines={2} maxWidth={200} className="font-semibold text-slate-800 text-[13px]" />
                          {c.last_event_at && <div className="text-[10px] text-slate-400 mt-0.5">Last event {formatDateTime(c.last_event_at)}</div>}
                        </div>
                        <ChevronRight size={12} className="text-slate-300" />
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right text-slate-700">{c.sent.toLocaleString()}</td>
                    <td className="px-3 py-3 text-right text-slate-700">{c.delivered.toLocaleString()}</td>
                    <td className="px-3 py-3 text-right font-semibold" style={{ color: '#3b82f6' }}>{c.open_rate}%</td>
                    <td className="px-3 py-3 text-right font-semibold" style={{ color: '#6366f1' }}>{c.click_rate}%</td>
                    <td className="px-3 py-3 text-right font-semibold text-slate-600">{c.ctor}%</td>
                    <td className="px-3 py-3 text-right font-semibold" style={{ color: c.bounce_rate > 5 ? '#ef4444' : '#64748b' }}>{c.bounces.toLocaleString()}</td>
                    <td className="px-3 py-3 text-right text-slate-700" data-testid={`unsubs-${c.campaign_id}`}>{(c.unsubscribes || 0).toLocaleString()}</td>
                    <td className="px-3 py-3 text-right font-semibold" style={{ color: c.complaints > 0 ? '#dc2626' : '#64748b' }} data-testid={`complaints-${c.campaign_id}`}>{(c.complaints || 0).toLocaleString()}</td>
                    <td className="px-3 py-3 min-w-[220px]">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[9px] uppercase text-slate-400 w-10">Open</span>
                          <div className="flex-1"><Bar pct={c.open_rate} color="#3b82f6" /></div>
                          <span className="text-[10px] text-slate-500 w-8 text-right">{c.unique_opens}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[9px] uppercase text-slate-400 w-10">Click</span>
                          <div className="flex-1"><Bar pct={c.click_rate} color="#6366f1" /></div>
                          <span className="text-[10px] text-slate-500 w-8 text-right">{c.unique_clicks}</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 text-[11px] text-slate-400">
        <TrendingUp size={11} className="inline -mt-0.5 mr-1" />
        Rates are based on delivered emails. <strong>CTOR</strong> is click-to-open ratio — how compelling the email body is once someone opens it.
      </div>

      {/* Per-link click report */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mt-6" data-testid="link-report">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Link2 size={14} style={{ color: '#6BA82E' }} />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>Link clicks</div>
              <div className="text-sm text-slate-500">Which links people clicked, most clicked first</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-56 max-w-[260px]">
              <PickSelect
                label="Campaign"
                labelClassName="sr-only"
                value={linkCampaign}
                onChange={setLinkCampaign}
                options={[
                  { value: '', label: 'All campaigns' },
                  ...(linkCampaign && !campaigns.some((c) => c.campaign_id === linkCampaign) ? [{ value: linkCampaign, label: campaignLabel(linkCampaign, abTests) }] : []),
                  ...campaigns.map((c) => ({ value: c.campaign_id, label: campaignLabel(c.campaign_id, abTests) })),
                ]}
                testId="link-campaign"
              />
            </div>
            <button onClick={exportLinks} disabled={links.links.length === 0} data-testid="export-links" className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
              <Download size={12} /> Export CSV
            </button>
          </div>
        </div>
        {linksLoading ? (
          <div className="p-8 flex items-center justify-center gap-2 text-slate-500 text-sm"><Loader2 size={14} className="animate-spin" /> Loading links…</div>
        ) : links.links.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="links-empty">No link clicks in this range yet.</div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="text-left px-4 py-2.5 font-semibold">Link</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Clicks</th>
                  <th className="text-right px-3 py-2.5 font-semibold">People</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {links.links.map((l, i) => (
                  <tr key={l.url} data-testid={`link-row-${i}`}>
                    <td className="px-4 py-2.5 text-[12px] text-slate-700"><LongTextCell id={`link-${i}`} text={l.url} lines={1} maxWidth={520} /></td>
                    <td className="px-3 py-2.5 text-right font-semibold" style={{ color: '#6366f1' }}>{l.clicks.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-right text-slate-700">{l.unique_clicks.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {links.links.length >= links.limit && <div className="px-4 py-2 text-[11px] text-slate-400" data-testid="links-capped">Showing the {links.limit} most clicked links.</div>}
          </>
        )}
      </div>

      {/* Modals */}
      {drillDownId && <CampaignDrillDown campaignId={drillDownId} label={campaignLabel(drillDownId, abTests)} onClose={() => setDrillDownId(null)} />}
      {showAbModal && <ABTestModal onClose={() => setShowAbModal(false)} onCreated={load} />}
    </div>
  );
}
